/**
 * EditMemberModal - Edit role, groups, supervisor, center, and quick actions for a member
 */

import { useState, useEffect, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import styled from 'styled-components';
import { AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Modal } from '../../components/ui/Modal';
import { Button } from '../../components/ui/Button';
import { TempPasswordModal } from '../../components/ui/TempPasswordModal';
import roleService from '../../services/api/roleService';
import groupService from '../../services/api/groupService';
import userService, { Member } from '../../services/api/userService';
import memberService, { isSignInReenableFailure } from '../../services/api/memberService';
import { extractApiError } from '../../utils/apiError';
import { getLocations } from '../../services/api/locationService';
import { useAuthStore } from '../../stores/authStore';
import { getDescendantIds } from './supervisionSubtree';

// ============================================================================
// Types
// ============================================================================

interface EditMemberModalProps {
  member: Member;
  isOpen: boolean;
  onClose: () => void;
  /** Raise the overlay z-index when this modal is opened above another high-z overlay
   *  (e.g. TeamMemberDetailDrawer at zIndex.modal=1050). Forwarded to <Modal zIndex>. */
  zIndex?: number;
}

// ============================================================================
// Styled Components
// ============================================================================

const FormSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[2]};
  margin-bottom: ${({ theme }) => theme.spacing[5]};
`;

const FieldLabel = styled.label`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const SelectField = styled.select`
  height: 44px;
  padding: 0 ${({ theme }) => theme.spacing[3]};
  border: 1px solid ${({ theme }) => theme.colors.borders.light};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: ${({ theme }) => theme.colors.backgrounds.white};
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  width: 100%;
  cursor: pointer;
  transition: ${({ theme }) => theme.transitions.fast};

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary.base};
    box-shadow: ${({ theme }) => theme.shadows.ring};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    background: ${({ theme }) => theme.colors.backgrounds.light};
  }
`;

const CheckboxGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[2]};
  padding: ${({ theme }) => theme.spacing[3]};
  border: 1px solid ${({ theme }) => theme.colors.borders.light};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  max-height: 160px;
  overflow-y: auto;
`;

const CheckboxRow = styled.label`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.primary};
  cursor: pointer;

  input[type='checkbox'] {
    width: 16px;
    height: 16px;
    accent-color: ${({ theme }) => theme.colors.primary.base};
    cursor: pointer;
  }
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid ${({ theme }) => theme.colors.borders.light};
  margin: ${({ theme }) => theme.spacing[5]} 0;
`;

const QuickActionsTitle = styled.h4`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin: 0 0 ${({ theme }) => theme.spacing[3]};
`;

const QuickActionsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing[2]};
`;

const AmberButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing[2]};
  height: 32px;
  padding: 0 ${({ theme }) => theme.spacing[3]};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  border: 1px solid ${({ theme }) => theme.colors.status.warning.border};
  background: transparent;
  color: ${({ theme }) => theme.colors.status.warning.text};
  cursor: pointer;
  transition: ${({ theme }) => theme.transitions.fast};

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.status.warning.bg};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ConfirmBanner = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing[3]};
  padding: ${({ theme }) => theme.spacing[3]};
  background: ${({ theme }) => theme.colors.status.error.bg};
  border: 1px solid ${({ theme }) => theme.colors.status.error.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  margin-bottom: ${({ theme }) => theme.spacing[4]};

  svg {
    flex-shrink: 0;
    width: 18px;
    height: 18px;
    color: ${({ theme }) => theme.colors.status.error.text};
    margin-top: 1px;
  }
`;

const ConfirmText = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.status.error.text};
  margin: 0;
`;

const ConfirmActions = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing[2]};
  margin-top: ${({ theme }) => theme.spacing[2]};
`;

const ModalFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing[3]};
  padding-top: ${({ theme }) => theme.spacing[4]};
  border-top: 1px solid ${({ theme }) => theme.colors.borders.light};
  margin-top: ${({ theme }) => theme.spacing[2]};
`;

const EmptyText = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const ErrorText = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.status.error.text};
  margin: ${({ theme }) => theme.spacing[2]} 0 0;
`;

// ============================================================================
// Component
// ============================================================================

type ConfirmAction = 'deactivate' | 'reset-password' | null;

export const EditMemberModal = ({ member, isOpen, onClose, zIndex }: EditMemberModalProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { hasPermission } = useAuthStore();
  // Deactivate/Reactivate disable the member's Entra sign-in, so they require the
  // org-admin-only 'team:manage:actions' permission (same tier as the departure wizard).
  const canDeactivate = hasPermission('team:manage:actions');
  const canActivate = hasPermission('team:manage:actions');
  const canResetPassword = hasPermission('team:manage');

  // Form state
  const [roleId, setRoleId] = useState(member.roles?.[0]?.id ?? '');
  const [selectedGroupIds, setSelectedGroupIds] = useState<string[]>(
    member.groups?.map((g) => g.id) ?? [],
  );
  const [supervisorId, setSupervisorId] = useState(member.supervisorId ?? '');
  const [primaryCenterId, setPrimaryCenterId] = useState(member.primaryCenterId ?? '');
  const [confirmAction, setConfirmAction] = useState<ConfirmAction>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  // Last reactivation failed because sign-in couldn't be re-enabled at the identity
  // provider (member NOT reactivated) — the button becomes a retry.
  const [signInReenableFailed, setSignInReenableFailed] = useState(false);
  const [tempPassword, setTempPassword] = useState<string | null>(null);

  // Sync state when member prop changes (e.g., opening modal for different member)
  useEffect(() => {
    setRoleId(member.roles?.[0]?.id ?? '');
    setSelectedGroupIds(member.groups?.map((g) => g.id) ?? []);
    setSupervisorId(member.supervisorId ?? '');
    setPrimaryCenterId(member.primaryCenterId ?? '');
    setConfirmAction(null);
    setSaveError(null);
    setSignInReenableFailed(false);
    setTempPassword(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [member.id]);

  // Data fetching
  const { data: rolesData } = useQuery({
    queryKey: ['roles'],
    queryFn: () => roleService.getRoles({ limit: 100 }),
    enabled: isOpen,
  });

  const { data: groupsData } = useQuery({
    queryKey: ['groups'],
    queryFn: () => groupService.getGroups({ limit: 100 }),
    enabled: isOpen,
  });

  const { data: membersData } = useQuery({
    queryKey: ['members-all'],
    queryFn: () => userService.getMembers({ limit: 200, status: 'active' }),
    enabled: isOpen,
  });

  const { data: locationsData } = useQuery({
    queryKey: ['locations', 'all-for-members'],
    queryFn: () => getLocations({ limit: 200 }),
    enabled: isOpen,
  });

  const roles = rolesData?.data ?? [];
  const groups = groupsData?.data ?? [];
  // Exclude the member themselves AND everyone beneath them: making a subordinate their
  // supervisor would close a loop, which the server rejects (#505). Mirrors the Hierarchy
  // tab and the departure wizard; the backend guard stays authoritative.
  const allMembers = useMemo(() => membersData?.data ?? [], [membersData]);
  const eligibleSupervisors = useMemo(() => {
    const subordinates = getDescendantIds(allMembers, member.id);
    return allMembers.filter((m) => m.id !== member.id && !subordinates.has(m.id));
  }, [allMembers, member.id]);
  const locations = locationsData?.data ?? [];

  // Mutations
  const saveMutation = useMutation({
    mutationFn: (payload: {
      roleIds?: string[];
      groupIds?: string[];
      supervisorId?: string | null;
      primaryCenterId?: string | null;
    }) => memberService.updateMember(member.id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['members'] });
      onClose();
    },
    onError: (error) => {
      // Prefer the server's 400 — a supervision-cycle rejection (#505) names both
      // members and the direction of the existing relationship, which the generic
      // string discards and leaves the admin with no way to act on.
      setSaveError(extractApiError(error, 'Failed to save changes. Please try again.'));
    },
  });

  const deactivateMutation = useMutation({
    mutationFn: () => memberService.deactivateMember(member.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['members'] });
      onClose();
    },
    onError: (err) => {
      setSaveError(extractApiError(err, 'Failed to deactivate member. Please try again.'));
      setConfirmAction(null);
    },
  });

  const reactivateMutation = useMutation({
    mutationFn: () => memberService.reactivateMember(member.id),
    onSuccess: () => {
      setSignInReenableFailed(false);
      queryClient.invalidateQueries({ queryKey: ['members'] });
      onClose();
    },
    onError: (err) => {
      setSignInReenableFailed(isSignInReenableFailure(err));
      setSaveError(extractApiError(err, 'Failed to reactivate member. Please try again.'));
    },
  });

  const resetPasswordMutation = useMutation({
    mutationFn: () => memberService.resetMemberPassword(member.id),
    onSuccess: (result) => {
      setConfirmAction(null);
      setTempPassword(result.temporaryPassword);
    },
    onError: () => {
      setSaveError(t('editMember.resetPassword.errorText'));
      setConfirmAction(null);
    },
  });

  const toggleGroup = (groupId: string) => {
    setSelectedGroupIds((prev) =>
      prev.includes(groupId) ? prev.filter((id) => id !== groupId) : [...prev, groupId],
    );
  };

  const handleSave = () => {
    setSaveError(null);
    saveMutation.mutate({
      roleIds: roleId ? [roleId] : [],
      groupIds: selectedGroupIds,
      supervisorId: supervisorId || null,
      primaryCenterId: primaryCenterId || null,
    });
  };

  const handleClose = () => {
    setConfirmAction(null);
    setSaveError(null);
    setSignInReenableFailed(false);
    setTempPassword(null);
    onClose();
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        title={`Edit Member — ${member.firstName} ${member.lastName}`}
        size="sm"
        zIndex={zIndex}
      >
        {confirmAction === 'deactivate' && (
          <ConfirmBanner>
            <AlertTriangle />
            <div>
              <ConfirmText>
                Deactivate{' '}
                <strong>
                  {member.firstName} {member.lastName}
                </strong>
                ? They will lose access immediately.
              </ConfirmText>
              <ConfirmActions>
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => deactivateMutation.mutate()}
                  isLoading={deactivateMutation.isPending}
                >
                  Confirm Deactivate
                </Button>
                <Button variant="secondary" size="sm" onClick={() => setConfirmAction(null)}>
                  Cancel
                </Button>
              </ConfirmActions>
            </div>
          </ConfirmBanner>
        )}

        {confirmAction === 'reset-password' && (
          <ConfirmBanner>
            <AlertTriangle />
            <div>
              <ConfirmText>
                {t('editMember.resetPassword.confirmText', {
                  name: `${member.firstName} ${member.lastName}`,
                })}
              </ConfirmText>
              <ConfirmActions>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => resetPasswordMutation.mutate()}
                  isLoading={resetPasswordMutation.isPending}
                >
                  {t('editMember.resetPassword.confirmButton')}
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setConfirmAction(null)}
                  disabled={resetPasswordMutation.isPending}
                >
                  {t('editMember.resetPassword.cancelButton')}
                </Button>
              </ConfirmActions>
            </div>
          </ConfirmBanner>
        )}

        <FormSection>
          <FieldLabel>Role</FieldLabel>
          <SelectField value={roleId} onChange={(e) => setRoleId(e.target.value)}>
            <option value="">No role</option>
            {roles.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
              </option>
            ))}
          </SelectField>
        </FormSection>

        <FormSection>
          <FieldLabel>Group Memberships</FieldLabel>
          {groups.length === 0 ? (
            <EmptyText>No groups available</EmptyText>
          ) : (
            <CheckboxGrid>
              {groups.map((g) => (
                <CheckboxRow key={g.id}>
                  <input
                    type="checkbox"
                    checked={selectedGroupIds.includes(g.id)}
                    onChange={() => toggleGroup(g.id)}
                  />
                  {g.name}
                </CheckboxRow>
              ))}
            </CheckboxGrid>
          )}
        </FormSection>

        <FormSection>
          <FieldLabel>Supervisor</FieldLabel>
          <SelectField value={supervisorId} onChange={(e) => setSupervisorId(e.target.value)}>
            <option value="">None</option>
            {eligibleSupervisors.map((m) => (
              <option key={m.id} value={m.id}>
                {m.firstName} {m.lastName}
                {m.roles?.[0] ? ` (${m.roles[0].name})` : ''}
              </option>
            ))}
          </SelectField>
        </FormSection>

        <FormSection>
          <FieldLabel>Location</FieldLabel>
          <SelectField value={primaryCenterId} onChange={(e) => setPrimaryCenterId(e.target.value)}>
            <option value="">No center</option>
            {locations.map((loc) => (
              <option key={loc.id} value={loc.id}>
                {loc.name}
              </option>
            ))}
          </SelectField>
        </FormSection>

        {saveError && <ErrorText>{saveError}</ErrorText>}

        <Divider />

        <QuickActionsTitle>Quick Actions</QuickActionsTitle>
        <QuickActionsRow>
          {member.status === 'active'
            ? canDeactivate && (
                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => setConfirmAction('deactivate')}
                  disabled={confirmAction !== null}
                >
                  Deactivate Member
                </Button>
              )
            : canActivate && (
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => {
                    setSaveError(null);
                    reactivateMutation.mutate();
                  }}
                  isLoading={reactivateMutation.isPending}
                >
                  {signInReenableFailed ? 'Retry sign-in re-enable' : 'Reactivate Member'}
                </Button>
              )}
          {canResetPassword && (
            <AmberButton
              onClick={() => setConfirmAction('reset-password')}
              disabled={confirmAction !== null}
            >
              Reset Password
            </AmberButton>
          )}
        </QuickActionsRow>

        <ModalFooter>
          <Button variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            isLoading={saveMutation.isPending}
            disabled={saveMutation.isPending}
          >
            Save Changes
          </Button>
        </ModalFooter>
      </Modal>

      <TempPasswordModal
        isOpen={tempPassword !== null}
        onClose={() => setTempPassword(null)}
        temporaryPassword={tempPassword}
        userLabel={`${member.firstName} ${member.lastName}`}
      />
    </>
  );
};

export default EditMemberModal;
